/**
 * Końcowy kontakt pozwala najpierw wybrać dogodny sposób rozmowy.
 */
import { useEffect, useState } from "react";
import { CalendarCheck2, ChevronLeft, Mail, Phone } from "lucide-react";
import { contact } from "@/lib/site-data";
import { trackConversion } from "@/lib/analytics";
import { Bitrix24InlineForm } from "./Bitrix24InlineForm";
import "./FinalContactBlock.css";

type ContactMode = "booking" | "message" | null;

export function FinalContactBlock() {
  const [mode, setMode] = useState<ContactMode>(null);
  const goBack = () => setMode(null);

  useEffect(() => {
    if (window.location.hash !== "#kontakt") return;
    let active = true;
    // Sekcja jest ładowana asynchronicznie. Dopiero po jej zamontowaniu
    // i ustaleniu metryk czcionek dopasowujemy punkt wejścia do kart.
    document.fonts.ready.then(() => {
      window.requestAnimationFrame(() => {
        if (active && window.location.hash === "#kontakt") {
          window.dispatchEvent(new Event("hashchange"));
        }
      });
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="kontakt"
      className="final-contact section-pad"
      aria-labelledby="contact-options-title"
    >
      <div className="shell-wide">
        <div className="section-lead final-contact-lead">
          <span className="section-kicker light-kicker">
            kontakt na Twoich zasadach
          </span>
          <h2 id="contact-options-title">
            Zadzwoń lub napisz, a dowiesz się więcej!
          </h2>
          <p>
            Nie musisz znać wszystkich kwot ani tłumaczyć wszystkiego od
            pierwszej minuty. Najpierw wybierz najłatwiejszy dla Ciebie sposób
            kontaktu.
          </p>
        </div>
        {!mode ? (
          <div
            className="contact-choice-grid"
            role="group"
            aria-label="Wybór sposobu kontaktu"
          >
            <a
              className="contact-choice phone-choice"
              href={contact.phoneHref}
              aria-label={`Zadzwoń teraz: ${contact.phoneDisplay}`}
              onClick={() =>
                trackConversion("contact_phone_click", {
                  placement: "final_contact",
                })
              }
            >
              <span className="contact-path-icon">
                <Phone size={24} />
              </span>
              <span className="choice-type">telefon</span>
              <strong>Chcę porozmawiać teraz</strong>
              <small>
                Anonimowo, spokojnie. Jesteśmy dostępni w dni robocze 8:00-20:00
                i zwykle oddzwaniamy w 15 minut.
              </small>
              <span className="choice-action">{contact.phoneDisplay}</span>
            </a>
            <button
              className="contact-choice booking-choice"
              type="button"
              onClick={() => setMode("booking")}
            >
              <span className="frequent-badge">Najczęściej wybierane</span>
              <span className="contact-path-icon">
                <CalendarCheck2 size={24} />
              </span>
              <span className="choice-type">spotkanie</span>
              <strong>Chcę bezpłatną konsultację</strong>
              <small>
                Stacjonarnie w kancelarii albo online - wybierzesz formę i
                termin.
              </small>
              <span className="choice-action">Chcę spotkanie w kancelarii</span>
            </button>
            <button
              className="contact-choice message-choice"
              type="button"
              onClick={() => setMode("message")}
            >
              <span className="contact-path-icon">
                <Mail size={24} />
              </span>
              <span className="choice-type">wiadomość</span>
              <strong>Na razie chcę napisać</strong>
              <small>
                Krótko opisz sytuację i zdecyduj, czy odpowiedzieć telefonem,
                e-mailem czy SMS-em.
              </small>
              <span className="choice-action">Chcę napisać</span>
            </button>
          </div>
        ) : (
          <div className="contact-mode-panel">
            <button type="button" className="contact-back" onClick={goBack}>
              <ChevronLeft size={18} /> Wróć do wyboru kontaktu
            </button>
            {mode === "booking" && (
              <article className="contact-path booking-path">
                <span className="frequent-badge">Najczęściej wybierane</span>
                <span className="contact-path-icon">
                  <CalendarCheck2 size={24} />
                </span>
                <Bitrix24InlineForm type="consultation" />
              </article>
            )}
            {mode === "message" && (
              <article className="contact-path message-path">
                <span className="contact-path-icon">
                  <Mail size={24} />
                </span>
                <Bitrix24InlineForm type="contact" />
              </article>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
